(() => {
  const BUILDER_SELECTOR = '[data-ag-mm-builder]';

  const normalizeShopRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const initBuilder = (root) => {
    if (!root || root.dataset.agMmInitialized === 'true') {
      return;
    }

    root.dataset.agMmInitialized = 'true';

    const cards = Array.from(
      root.querySelectorAll('[data-ag-mm-card]')
    );

    const charmCards = cards.filter(
      (card) => card.dataset.role === 'charm'
    );

    const addButton = root.querySelector('[data-ag-mm-add]');
    const addLabel = root.querySelector('[data-ag-mm-add-label]');
    const basisCount = root.querySelector('[data-ag-mm-basis-count]');
    const charmCount = root.querySelector('[data-ag-mm-charm-count]');
    const itemCount = root.querySelector('[data-ag-mm-item-count]');
    const totalElement = root.querySelector('[data-ag-mm-total]');
    const statusElement = root.querySelector('[data-ag-mm-status]');
    const selectionList = root.querySelector(
      '[data-ag-mm-selection-list]'
    );

    const searchInput = root.querySelector('[data-ag-mm-search]');
    const visibleCount = root.querySelector(
      '[data-ag-mm-visible-count]'
    );
    const noResults = root.querySelector('[data-ag-mm-no-results]');

    if (!cards.length || !addButton) {
      return;
    }

    const locale =
      root.dataset.locale ||
      document.documentElement.lang ||
      'de-DE';

    const currency = root.dataset.currency || 'EUR';

    const texts = {
      missingBasis:
        root.dataset.textMissingBasis ||
        'Bitte wähle mindestens ein Basisstück.',

      missingCharm:
        root.dataset.textMissingCharm ||
        'Bitte wähle mindestens einen Charm.',

      addError:
        root.dataset.textAddError ||
        'Das Set konnte nicht hinzugefügt werden.',

      add:
        root.dataset.textAdd ||
        'Set in den Warenkorb',

      adding:
        root.dataset.textAdding ||
        'Wird hinzugefügt …',

      remove:
        root.dataset.textRemove ||
        'Entfernen',
    };

    const moneyFormatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    });

    const moneyFormat = root.dataset.moneyFormat || '';
    const moneyPlaceholder = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const moneyFormats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_no_decimals_with_space_separator: [0, ' ', '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
    };
    const moneyOptions = moneyPlaceholder
      ? moneyFormats[moneyPlaceholder[1]]
      : null;

    const formatMoney = (cents) => {
      const value = (Number(cents) || 0) / 100;

      if (!moneyOptions || currency !== root.dataset.shopCurrency) {
        return moneyFormatter.format(value);
      }

      const [precision, thousands, decimal] = moneyOptions;
      const [whole, fraction] = value.toFixed(precision).split('.');
      const amount = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands)
        + (fraction ? decimal + fraction : '');

      return moneyFormat.replace(moneyPlaceholder[0], amount);
    };

    const normalizeText = (value) => {
      return String(value || '')
        .toLocaleLowerCase(locale)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
    };

    const getQuantity = (card) => {
      return Number(card.dataset.quantity || 0);
    };

    const cleanVariantTitle = (title) => {
      const value = String(title || '').trim();

      if (
        !value ||
        value.toLowerCase() === 'default title' ||
        value.toLowerCase() === 'standardtitel'
      ) {
        return '';
      }

      return value;
    };

    const getSelection = () => {
      return cards
        .map((card) => {
          const image = card.querySelector('.ag-mm-media__image');

          return {
            card,
            cardKey: card.dataset.cardKey || '',
            role: card.dataset.role || '',
            productId: Number(card.dataset.productId || 0),
            productTitle: card.dataset.productTitle || '',
            variantId: Number(card.dataset.selectedVariantId || 0),
            variantTitle: cleanVariantTitle(
              card.dataset.selectedVariantTitle
            ),
            price: Number(card.dataset.selectedPrice || 0),
            quantity: getQuantity(card),
            imageUrl: image?.currentSrc || image?.src || '',
          };
        })
        .filter((item) => {
          return item.quantity > 0 && item.variantId > 0;
        });
    };

    const calculateTotals = (selection) => {
      return selection.reduce(
        (totals, item) => {
          totals.items += item.quantity;
          totals.total += item.price * item.quantity;

          if (item.role === 'basis') {
            totals.basis += item.quantity;
          }

          if (item.role === 'charm') {
            totals.charms += item.quantity;
          }

          return totals;
        },
        {
          basis: 0,
          charms: 0,
          items: 0,
          total: 0,
        }
      );
    };

    const createSummaryItem = (item) => {
      const row = document.createElement('div');
      row.className = 'ag-mm-summary-item';

      const imageWrapper = document.createElement('div');
      imageWrapper.className = 'ag-mm-summary-item__image';

      if (item.imageUrl) {
        const image = document.createElement('img');
        image.src = item.imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        imageWrapper.appendChild(image);
      }

      const content = document.createElement('div');
      content.className = 'ag-mm-summary-item__content';

      const title = document.createElement('span');
      title.className = 'ag-mm-summary-item__title';
      title.textContent = item.productTitle;

      const meta = document.createElement('span');
      meta.className = 'ag-mm-summary-item__meta';

      const metaParts = [];

      if (item.variantTitle) {
        metaParts.push(item.variantTitle);
      }

      metaParts.push(`× ${item.quantity}`);
      meta.textContent = metaParts.join(' · ');

      const price = document.createElement('span');
      price.className = 'ag-mm-summary-item__price';
      price.textContent = formatMoney(
        item.price * item.quantity
      );

      content.append(title, meta, price);

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'ag-mm-summary-item__remove';
      removeButton.dataset.agMmRemoveSelection = item.cardKey;
      removeButton.setAttribute(
        'aria-label',
        `${texts.remove}: ${item.productTitle}`
      );
      removeButton.title = texts.remove;
      removeButton.textContent = '×';

      row.append(imageWrapper, content, removeButton);

      return row;
    };

    const renderSelectionList = (selection) => {
      if (!selectionList) {
        return;
      }

      selectionList.replaceChildren();

      selection.forEach((item) => {
        selectionList.appendChild(createSummaryItem(item));
      });
    };

    const updateSummary = () => {
      const selection = getSelection();
      const totals = calculateTotals(selection);

      if (basisCount) {
        basisCount.textContent = String(totals.basis);
      }

      if (charmCount) {
        charmCount.textContent = String(totals.charms);
      }

      if (itemCount) {
        itemCount.textContent = String(totals.items);
      }

      if (totalElement) {
        totalElement.textContent = formatMoney(totals.total);
      }

      renderSelectionList(selection);

      const valid =
        totals.basis >= 1 &&
        totals.charms >= 1;

      addButton.disabled =
        !valid ||
        root.classList.contains('is-loading');

      if (
        statusElement &&
        !root.classList.contains('has-submit-error')
      ) {
        if (totals.basis < 1) {
          statusElement.textContent = texts.missingBasis;
        } else if (totals.charms < 1) {
          statusElement.textContent = texts.missingCharm;
        } else {
          statusElement.textContent = '';
        }
      }
    };

    const setQuantity = (card, nextQuantity) => {
      const quantity = Math.max(
        0,
        Math.floor(Number(nextQuantity) || 0)
      );

      card.dataset.quantity = String(quantity);

      const output = card.querySelector(
        '[data-ag-mm-quantity-value]'
      );

      const decreaseButton = card.querySelector(
        '[data-ag-mm-decrease]'
      );

      if (output) {
        output.textContent = String(quantity);
      }

      if (decreaseButton) {
        decreaseButton.disabled = quantity <= 0;
      }

      card.classList.toggle(
        'is-selected',
        quantity > 0
      );

      updateSummary();
    };

    const updateCardVariant = ({
      card,
      variantId,
      variantTitle,
      price,
      imageUrl,
    }) => {
      card.dataset.selectedVariantId = String(
        variantId || ''
      );

      card.dataset.selectedVariantTitle = String(
        variantTitle || ''
      );

      card.dataset.selectedPrice = String(
        Number(price) || 0
      );

      const priceElement = card.querySelector(
        '[data-ag-mm-price]'
      );

      if (priceElement) {
        priceElement.textContent = formatMoney(price);
      }

      const image = card.querySelector(
        '.ag-mm-media__image'
      );

      if (image && imageUrl) {
        image.src = imageUrl;
        image.removeAttribute('srcset');
      }

      updateSummary();
    };

    const setLoading = (loading) => {
      root.classList.toggle('is-loading', loading);

      addButton.setAttribute(
        'aria-busy',
        loading ? 'true' : 'false'
      );

      if (addLabel) {
        addLabel.textContent = loading
          ? texts.adding
          : texts.add;
      }

      updateSummary();
    };

    const showSubmitError = (message) => {
      root.classList.add('has-submit-error');

      if (statusElement) {
        statusElement.textContent =
          message || texts.addError;
      }

      root
        .querySelector('[data-ag-mm-summary]')
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
    };

    const filterCharms = () => {
      if (!searchInput) {
        return;
      }

      const query = normalizeText(searchInput.value);
      let visible = 0;

      charmCards.forEach((card) => {
        const searchableText = normalizeText(
          card.dataset.searchText
        );

        const matches =
          !query ||
          searchableText.includes(query);

        card.hidden = !matches;

        if (matches) {
          visible += 1;
        }
      });

      if (visibleCount) {
        visibleCount.textContent =
          `${visible} / ${charmCards.length}`;
      }

      if (noResults) {
        noResults.hidden = visible !== 0;
      }
    };

    root.addEventListener('click', (event) => {
      const removeSelection = event.target.closest(
        '[data-ag-mm-remove-selection]'
      );

      if (
        removeSelection &&
        root.contains(removeSelection)
      ) {
        const cardKey =
          removeSelection.dataset.agMmRemoveSelection;

        const card = cards.find(
          (item) => item.dataset.cardKey === cardKey
        );

        if (card) {
          setQuantity(card, 0);
        }

        return;
      }

      const variantButton = event.target.closest(
        '[data-ag-mm-variant]'
      );

      if (
        variantButton &&
        root.contains(variantButton) &&
        !variantButton.disabled
      ) {
        const card = variantButton.closest(
          '[data-ag-mm-card]'
        );

        if (!card) {
          return;
        }

        card
          .querySelectorAll('[data-ag-mm-variant]')
          .forEach((button) => {
            const selected =
              button === variantButton;

            button.classList.toggle(
              'is-selected',
              selected
            );

            button.setAttribute(
              'aria-pressed',
              selected ? 'true' : 'false'
            );
          });

        updateCardVariant({
          card,
          variantId: variantButton.dataset.variantId,
          variantTitle:
            variantButton.dataset.variantTitle,
          price: variantButton.dataset.price,
          imageUrl: variantButton.dataset.image,
        });

        return;
      }

      const increaseButton = event.target.closest(
        '[data-ag-mm-increase]'
      );

      if (
        increaseButton &&
        root.contains(increaseButton) &&
        !increaseButton.disabled
      ) {
        const card = increaseButton.closest(
          '[data-ag-mm-card]'
        );

        if (card) {
          setQuantity(
            card,
            getQuantity(card) + 1
          );
        }

        return;
      }

      const decreaseButton = event.target.closest(
        '[data-ag-mm-decrease]'
      );

      if (
        decreaseButton &&
        root.contains(decreaseButton) &&
        !decreaseButton.disabled
      ) {
        const card = decreaseButton.closest(
          '[data-ag-mm-card]'
        );

        if (card) {
          setQuantity(
            card,
            getQuantity(card) - 1
          );
        }
      }
    });

    root.addEventListener('change', (event) => {
      const select = event.target.closest(
        '[data-ag-mm-variant-select]'
      );

      if (!select || !root.contains(select)) {
        return;
      }

      const card = select.closest(
        '[data-ag-mm-card]'
      );

      const option =
        select.options[select.selectedIndex];

      if (!card || !option) {
        return;
      }

      updateCardVariant({
        card,
        variantId: option.value,
        variantTitle:
          option.dataset.variantTitle ||
          option.textContent,
        price: option.dataset.price,
        imageUrl: option.dataset.image,
      });
    });

    if (searchInput) {
      searchInput.addEventListener(
        'input',
        filterCharms
      );

      searchInput.addEventListener(
        'search',
        filterCharms
      );
    }

    addButton.addEventListener(
      'click',
      async () => {
        root.classList.remove('has-submit-error');

        const selection = getSelection();
        const totals = calculateTotals(selection);

        if (totals.basis < 1) {
          showSubmitError(texts.missingBasis);
          return;
        }

        if (totals.charms < 1) {
          showSubmitError(texts.missingCharm);
          return;
        }

        const bundleId =
          `AG-MM-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)
            .toUpperCase()}`;

        const bundleName =
          root.dataset.mainProductTitle ||
          'AuraGleam Charm Studio';

        const items = selection.map((item) => ({
          id: item.variantId,
          quantity: item.quantity,
          properties: {
            _ag_bundle_id: bundleId,
            _ag_bundle_name: bundleName,
            _ag_bundle_role: item.role,
          },
        }));

        setLoading(true);

        try {
          const shopRoot = normalizeShopRoot();

          const response = await fetch(
            `${shopRoot}cart/add.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({
                items,
              }),
            }
          );

          const payload = await response
            .json()
            .catch(() => ({}));

          if (!response.ok) {
            throw new Error(
              payload.description ||
              payload.message ||
              texts.addError
            );
          }

          window.location.assign(
            `${shopRoot}cart`
          );
        } catch (error) {
          setLoading(false);

          showSubmitError(
            error?.message ||
            texts.addError
          );
        }
      }
    );

    cards.forEach((card) => {
      card.dataset.quantity = '0';

      const output = card.querySelector(
        '[data-ag-mm-quantity-value]'
      );

      const decreaseButton = card.querySelector(
        '[data-ag-mm-decrease]'
      );

      if (output) {
        output.textContent = '0';
      }

      if (decreaseButton) {
        decreaseButton.disabled = true;
      }

      card.classList.remove('is-selected');
    });

    filterCharms();
    updateSummary();
  };

  const initAllBuilders = (scope = document) => {
    scope
      .querySelectorAll(BUILDER_SELECTOR)
      .forEach(initBuilder);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAllBuilders()
    );
  } else {
    initAllBuilders();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAllBuilders(event.target);
    }
  );
})();